import {
  MassActionData,
  MassActionsContextProvider,
} from '@components/MassActions/contexts/MassActions.context';
import { ReactNode, createContext, useContext } from 'react';
import CatalogMoveToSectionModal from '../components/CatalogMoveToSectionModal/CatalogMoveToSectionModal';
import { useCatalogMassActions } from '../hooks/useCatalogMassActions';
import {
  CatalogProductsSelection,
  useCatalogProductsSelection,
} from '../hooks/useCatalogProductsSelection';

export interface CatalogSelectionItem {
  productId: string;
  sectionProductId: string;
}

export interface CatalogMassActionsContextValue {
  readMode: boolean;
  isSelectionActive: boolean;
  isSelectionLocked: boolean;
  isSelected: (productId: string) => boolean;
  toggle: (item: CatalogSelectionItem) => void;
}

export const CatalogMassActionsContext = createContext<CatalogMassActionsContextValue>({
  readMode: true,
  isSelectionActive: false,
  isSelectionLocked: false,
  isSelected: () => false,
  toggle: () => undefined,
});

interface CatalogMassActionsProviderProps {
  readMode: boolean;
  totalItems: number;
  isDynamic: boolean;
  catalogId: string;
  canPersist: boolean;
  children: ReactNode;
}

interface CatalogMassActionsBridgeProps extends CatalogMassActionsProviderProps {
  selection: CatalogProductsSelection;
}

const CatalogMassActionsBridge = ({
  children,
  selection,
  ...params
}: CatalogMassActionsBridgeProps) => {
  const { contextValue, moveModal } = useCatalogMassActions({ ...params, selection });

  return (
    <CatalogMassActionsContext.Provider value={contextValue}>
      {children}
      <CatalogMoveToSectionModal {...moveModal} />
    </CatalogMassActionsContext.Provider>
  );
};

const CatalogMassActionsProvider = ({ children, ...params }: CatalogMassActionsProviderProps) => {
  const selection = useCatalogProductsSelection();

  const massActionsData: MassActionData<CatalogSelectionItem> = {
    listOrDefault: [],
    isAllItemsSelected: false,
    viewMode: { active: 'LIST' },
    list: {
      selecteds: selection.selecteds,
      changeSelecteds: selection.clearSelection,
      count: params.totalItems,
    },
    listActions: { toggleAllSelected: selection.clearSelection },
  };

  return (
    <MassActionsContextProvider data={massActionsData}>
      <CatalogMassActionsBridge {...params} selection={selection}>
        {children}
      </CatalogMassActionsBridge>
    </MassActionsContextProvider>
  );
};

export const useCatalogMassActionsContext = () => useContext(CatalogMassActionsContext);

export default CatalogMassActionsProvider;
